import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';

const STATUS_LABELS = {
  new: 'New Inquiry',
  under_review: 'Under Review',
  accepted: 'Accepted',
  awaiting_agreement: 'Agreement Sent',
  agreement_received: 'Acknowledged',
  awaiting_payment: 'Awaiting Payment',
  confirmed: 'Confirmed',
  completed: 'Completed',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

const STATUS_COLORS = {
  new: 'text-emerald-700',
  under_review: 'text-neutral-700',
  accepted: 'text-[#C8A15A]',
  awaiting_agreement: 'text-amber-700',
  agreement_received: 'text-blue-700',
  awaiting_payment: 'text-amber-700',
  confirmed: 'text-emerald-800',
  completed: 'text-neutral-500',
  declined: 'text-red-600',
  cancelled: 'text-red-500',
};

const money = (c, cur = 'usd') => new Intl.NumberFormat('en-US', { style: 'currency', currency: (cur || 'usd').toUpperCase() }).format((c || 0) / 100);
const dollarsToCents = (v) => {
  const n = Number(String(v).replace(/[^0-9.\-]/g, ''));
  if (!isFinite(n)) return 0;
  return Math.round(n * 100);
};
const centsToDollars = (c) => (c == null || c === '') ? '' : (Number(c) / 100).toFixed(2);

export default function SpeakingEngagementsAdmin() {
  const [data, setData] = useState({ items: [], total: 0, stats: {} });
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async (s = status, query = q) => {
    const params = {};
    if (s && s !== 'all') params.status = s;
    if (query?.trim()) params.q = query.trim();
    const r = await adminApi.get('/admin/speaking-engagements', { params });
    setData(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(status, q); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(() => load(status, q), 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div data-testid="admin-speaking-engagements-view">
      <header className="mb-8">
        <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500 mb-2">Speaking</div>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl lg:text-4xl text-neutral-900">Engagement Inquiries</h1>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-6">
        {['new', 'accepted', 'awaiting_agreement', 'agreement_received', 'confirmed', 'completed'].map((s) => (
          <div key={s} className="bg-white border border-neutral-200/60 rounded-xl px-4 py-3">
            <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">{STATUS_LABELS[s]}</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-2xl text-neutral-900 tabular-nums">{data.stats?.[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <input
          data-testid="speaking-search-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, org, event…"
          className="bg-white border border-neutral-200 rounded-full px-4 py-2 text-[13px] focus:outline-none focus:border-neutral-900 w-full lg:w-80"
        />
        <div className="flex gap-2 flex-wrap">
          {['all', 'new', 'under_review', 'accepted', 'awaiting_agreement', 'agreement_received', 'awaiting_payment', 'confirmed', 'completed', 'declined'].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`speaking-status-filter-${s}`}
              className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full ${status === s ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}>
              {s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-neutral-200/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[720px]">
            <thead className="bg-neutral-50/70">
              <tr>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Inquiry</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Event</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Status</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Fee</th>
                <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200/60">
              {data.items.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-500">No inquiries in this view yet.</td></tr>
              )}
              {data.items.map((e) => (
                <tr key={e.id} onClick={() => setSelected(e.id)} data-testid={`speaking-row-${e.id}`} className="cursor-pointer hover:bg-neutral-50/70">
                  <td className="px-5 py-3">
                    <div className="text-neutral-900">{e.name}</div>
                    <div className="text-neutral-500 text-[11px]">{e.email} · {e.organization || '—'}</div>
                  </td>
                  <td className="px-5 py-3 text-neutral-700 truncate max-w-[240px]">{e.event_name || '—'}<div className="text-neutral-500 text-[11px]">{e.event_date || ''}</div></td>
                  <td className="px-5 py-3">
                    <span className={`uppercase tracking-[0.18em] text-[10px] ${STATUS_COLORS[e.status] || 'text-neutral-500'}`}>{STATUS_LABELS[e.status] || e.status}</span>
                  </td>
                  <td className="px-5 py-3 text-neutral-700 tabular-nums">{e.engagement_terms?.speaking_fee_cents ? money(e.engagement_terms.speaking_fee_cents, e.engagement_terms.currency) : '—'}</td>
                  <td className="px-5 py-3 text-neutral-500 text-[11px]">{e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <EngagementDrawer id={selected} onClose={() => setSelected(null)} onChanged={() => load()} />}
    </div>
  );
}

function EngagementDrawer({ id, onClose, onChanged }) {
  const [eng, setEng] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [signedUrl, setSignedUrl] = useState('');

  const load = async () => {
    const r = await adminApi.get(`/admin/speaking-engagements/${id}`);
    setEng(r.data);
    setNotes(r.data.notes || '');
    setSignedUrl(r.data.signed_agreement_url || '');
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!eng) return null;
  const terms = eng.engagement_terms || {};
  const evidence = eng.agreement_evidence;

  const runAction = async (label, fn) => {
    setBusy(true);
    try { await fn(); toast.success(label); onChanged(); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed.`); }
    finally { setBusy(false); }
  };
  const accept = () => runAction('Accepted — email sent.', async () => {
    if (!window.confirm(`Accept ${eng.name}'s speaking inquiry? An acceptance email will be sent to ${eng.email}.`)) throw new Error('cancelled');
    await adminApi.post(`/admin/speaking-engagements/${id}/accept`);
  });
  const decline = () => runAction('Declined — email sent.', async () => {
    const message = window.prompt('Optional message to the client:') || '';
    await adminApi.post(`/admin/speaking-engagements/${id}/decline`, { message });
  });
  const sendAgreement = () => runAction('Agreement email sent.', async () => {
    if (!terms.speaking_fee_cents) throw Object.assign(new Error(''), { response: { data: { detail: 'Set engagement terms first.' } } });
    await adminApi.post(`/admin/speaking-engagements/${id}/send-agreement`, {});
  });
  const resendAgreement = () => runAction('Agreement re-sent.', async () => {
    if (!window.confirm(`Resend agreement email to ${eng.email}? They will receive a second email.`)) throw new Error('cancelled');
    await adminApi.post(`/admin/speaking-engagements/${id}/send-agreement`, { resend: true });
  });
  const sendBalance = () => runAction('Balance email sent.', async () => {
    await adminApi.post(`/admin/speaking-engagements/${id}/send-balance-request`);
  });
  const markComplete = () => runAction('Marked complete.', async () => {
    await adminApi.post(`/admin/speaking-engagements/${id}/complete`);
  });
  const saveNotes = () => runAction('Notes saved.', async () => {
    await adminApi.patch(`/admin/speaking-engagements/${id}/notes`, { notes });
  });
  const saveSignedUrl = () => runAction('Signed agreement URL saved.', async () => {
    await adminApi.post(`/admin/speaking-engagements/${id}/mark-signed-agreement`, { signed_agreement_url: signedUrl });
  });
  const copyToClipboard = async (url, label) => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); toast.success(`${label} copied.`); }
    catch { toast.error('Copy failed — please copy manually.'); }
  };

  const canAccept = ['new', 'under_review'].includes(eng.status);
  const canDecline = !['declined', 'completed', 'cancelled', 'confirmed'].includes(eng.status);
  const hasTerms = !!terms.speaking_fee_cents;
  // Send Agreement is available whenever terms are set and it hasn't been sent yet
  // (regardless of whether Accept was clicked — saving terms auto-advances status server-side).
  const canSendAgreement = hasTerms && !eng.agreement_sent_at && !['declined', 'cancelled', 'completed'].includes(eng.status);
  const canResendAgreement = !!eng.agreement_sent_at && !eng.agreement_acknowledged_at;
  const canSendBalance = eng.deposit_paid_at && (terms.balance_cents || 0) > 0 && !eng.balance_paid_at;

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-neutral-900/30 backdrop-blur-sm" />
      <aside data-testid="speaking-drawer" className="fixed right-0 top-0 z-50 h-full w-full lg:w-[760px] bg-[#FAF8F5] border-l border-neutral-200/70 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#FAF8F5]/95 backdrop-blur border-b border-neutral-200/60 px-6 lg:px-8 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-1">{STATUS_LABELS[eng.status] || eng.status}</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-2xl text-neutral-900 truncate">{eng.name} · {eng.organization}</div>
            <div className="text-neutral-500 text-[12px] truncate">{eng.email}{eng.phone ? ' · ' + eng.phone : ''}</div>
          </div>
          <button onClick={onClose} data-testid="speaking-drawer-close" className="text-neutral-500 hover:text-neutral-900 text-2xl px-2 shrink-0">×</button>
        </div>

        <div className="px-6 lg:px-8 py-6 space-y-6">
          {/* Next Step banner — makes the workflow obvious */}
          {(() => {
            let hint = null;
            if (['new', 'under_review'].includes(eng.status) && !hasTerms) hint = { text: 'Next step: Set Engagement Terms (fee & deposit), then Send Agreement.', tone: 'neutral' };
            else if (canSendAgreement) hint = { text: 'Next step: Send Agreement — the client will receive an email with the review & acknowledgment link.', tone: 'action' };
            else if (eng.agreement_sent_at && !eng.agreement_acknowledged_at) hint = { text: 'Waiting on client to review & acknowledge the agreement. Use Resend Agreement if they didn\u2019t receive it.', tone: 'wait' };
            else if (eng.agreement_acknowledged_at && !eng.deposit_paid_at && (terms.deposit_cents || 0) > 0) hint = { text: 'Client acknowledged the agreement. Waiting on deposit payment.', tone: 'wait' };
            else if (canSendBalance) hint = { text: 'Deposit received. Next step: Send Balance Payment Link.', tone: 'action' };
            else if (eng.status === 'confirmed') hint = { text: 'Fully paid. Mark Completed after the event.', tone: 'success' };
            if (!hint) return null;
            const cls = hint.tone === 'action' ? 'bg-[#C8A15A]/10 border-[#C8A15A]/40 text-[#8a6c30]'
                      : hint.tone === 'wait' ? 'bg-blue-50 border-blue-200 text-blue-800'
                      : hint.tone === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-neutral-100 border-neutral-200 text-neutral-700';
            return (
              <div data-testid="speaking-next-step-banner" className={`px-4 py-3 border rounded-lg text-[13px] ${cls}`}>
                <span className="uppercase tracking-[0.18em] text-[10px] mr-2">Next</span>{hint.text}
              </div>
            );
          })()}

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {canAccept && (
              <button onClick={accept} disabled={busy} data-testid="speaking-accept-btn"
                className="bg-emerald-700 text-white uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-emerald-800 disabled:opacity-50">
                Accept Inquiry
              </button>
            )}
            {canDecline && (
              <button onClick={decline} disabled={busy} data-testid="speaking-decline-btn"
                className="border border-red-500 text-red-600 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-red-50 disabled:opacity-50">
                Decline
              </button>
            )}
            <button onClick={() => setShowTerms(true)} disabled={busy} data-testid="speaking-edit-terms-btn"
              className="border border-neutral-300 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">
              {hasTerms ? 'Edit Engagement Terms' : 'Set Engagement Terms'}
            </button>
            {canSendAgreement && (
              <button onClick={sendAgreement} disabled={busy} data-testid="speaking-send-agreement-btn"
                className="bg-neutral-900 text-white uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-800 disabled:opacity-50">
                {busy ? 'Sending…' : 'Send Agreement'}
              </button>
            )}
            {canResendAgreement && (
              <button onClick={resendAgreement} disabled={busy} data-testid="speaking-resend-agreement-btn"
                className="border border-neutral-400 text-neutral-700 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100 disabled:opacity-50">
                {busy ? 'Sending…' : 'Resend Agreement'}
              </button>
            )}
            {canSendBalance && (
              <button onClick={sendBalance} disabled={busy} data-testid="speaking-send-balance-btn"
                className="bg-neutral-900 text-white uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-800 disabled:opacity-50">
                Send Balance Payment Link
              </button>
            )}
            {['confirmed'].includes(eng.status) && (
              <button onClick={markComplete} disabled={busy} data-testid="speaking-complete-btn"
                className="border border-neutral-300 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">
                Mark Completed
              </button>
            )}
          </div>

          {/* Inquiry snapshot */}
          <Section title="Inquiry">
            <Row label="Event Name" value={eng.event_name} />
            <Row label="Event Type" value={eng.event_type} />
            <Row label="Event Date" value={eng.event_date} />
            <Row label="Location" value={eng.event_location} />
            <Row label="Format" value={eng.event_format} />
            <Row label="Audience Size" value={eng.audience_size} />
            <Row label="Budget" value={eng.budget} />
            <Row label="Topic" value={eng.topic} />
            <Block label="Message" value={eng.message} />
          </Section>

          {/* Engagement Terms */}
          {hasTerms && (
            <Section title="Engagement Terms">
              <div className="grid grid-cols-3 gap-2">
                <Fee label="Speaking Fee" value={money(terms.speaking_fee_cents, terms.currency)} />
                <Fee label="Deposit" value={money(terms.deposit_cents, terms.currency)} accent />
                <Fee label="Balance" value={money(terms.balance_cents, terms.currency)} />
              </div>
              <div className="mt-4 space-y-1">
                <Row label="Event Date" value={terms.event_date} />
                <Row label="Location" value={terms.event_location} />
                <Row label="Format" value={terms.speaking_format} />
                <Row label="Duration" value={terms.speaking_duration} />
                <Row label="Deposit Due" value={terms.deposit_due_date} />
                <Row label="Balance Due" value={terms.balance_due_date} />
              </div>
              {(terms.travel_terms || terms.accommodation_terms || terms.cancellation_terms || terms.additional_terms) && (
                <div className="mt-3 space-y-2">
                  {terms.travel_terms && <Block label="Travel" value={terms.travel_terms} />}
                  {terms.accommodation_terms && <Block label="Accommodation" value={terms.accommodation_terms} />}
                  {terms.cancellation_terms && <Block label="Cancellation" value={terms.cancellation_terms} />}
                  {terms.additional_terms && <Block label="Additional" value={terms.additional_terms} />}
                </div>
              )}
              {terms.internal_notes && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-[12px] text-amber-900">
                  <div className="uppercase tracking-[0.18em] text-[9px] text-amber-700 mb-1">Internal Notes (not shown to client)</div>
                  <div className="whitespace-pre-wrap">{terms.internal_notes}</div>
                </div>
              )}
            </Section>
          )}

          {/* Agreement Status */}
          {eng.agreement_url && (
            <Section title="Agreement">
              <div className="p-4 bg-white border border-neutral-200 rounded-lg space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">Agreement Link</div>
                    <div className="text-[12px] text-neutral-800 break-all">{eng.agreement_url}</div>
                  </div>
                  <div className="flex gap-2">
                    <a href={eng.agreement_url} target="_blank" rel="noreferrer" data-testid="speaking-open-agreement-link"
                       className="border border-neutral-300 uppercase tracking-[0.18em] text-[10px] px-3 py-1.5 rounded-full hover:bg-neutral-100">Open</a>
                    <button onClick={() => copyToClipboard(eng.agreement_url, 'Agreement link')} data-testid="speaking-copy-agreement-link"
                       className="border border-neutral-300 uppercase tracking-[0.18em] text-[10px] px-3 py-1.5 rounded-full hover:bg-neutral-100">Copy</button>
                  </div>
                </div>
                {eng.agreement_sent_at && (
                  <div className="text-[12px] text-neutral-600" data-testid="speaking-agreement-sent-status">
                    ✓ Sent {new Date(eng.agreement_sent_at).toLocaleString()}
                    {eng.agreement_sent_count > 1 && ` · ${eng.agreement_sent_count} times`}
                    {eng.last_agreement_sent_at && eng.agreement_sent_count > 1 && (
                      <> · last on {new Date(eng.last_agreement_sent_at).toLocaleString()}</>
                    )}
                  </div>
                )}
                {eng.agreement_acknowledged_at && (
                  <div className="text-[12px] text-emerald-800" data-testid="speaking-agreement-acknowledged">
                    ✓ Client acknowledged {new Date(eng.agreement_acknowledged_at).toLocaleString()}
                  </div>
                )}
              </div>

              {/* Evidence */}
              {evidence && (
                <div className="mt-3 p-4 bg-white border border-neutral-200 rounded-lg">
                  <div className="uppercase tracking-[0.22em] text-[9px] text-neutral-500 mb-2">Acknowledgment Evidence</div>
                  <div className="text-[12px] text-neutral-800 mb-2">Accepted: <strong>{evidence.accepted ? 'Yes' : 'No'}</strong> · {evidence.accepted_at ? new Date(evidence.accepted_at).toLocaleString() : '—'}</div>
                  <div className="text-[11px] text-neutral-600 mb-2">IP: <span className="font-mono">{evidence.client_ip || '—'}</span></div>
                  <div className="text-[11px] text-neutral-600 mb-2 truncate">UA: <span className="font-mono">{evidence.user_agent || '—'}</span></div>
                  <div className="text-[11px] text-neutral-600">Policy Versions:</div>
                  <ul className="text-[11px] text-neutral-600 list-disc pl-5">
                    {Object.entries(evidence.policy_versions_accepted || {}).map(([k, v]) => <li key={k}>{k}: <strong>{v}</strong></li>)}
                  </ul>
                  <details className="mt-3">
                    <summary className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 cursor-pointer">View acknowledgment text</summary>
                    <p className="mt-2 text-[12px] text-neutral-700 italic leading-relaxed">{evidence.acknowledgment_text}</p>
                  </details>
                </div>
              )}
            </Section>
          )}

          {/* Signed Agreement URL (URL-paste MVP) */}
          {eng.agreement_acknowledged_at && (
            <Section title="Signed Agreement (URL-paste)">
              <p className="text-[12px] text-neutral-600 mb-2">Paste a private Google Drive / Dropbox / other document link to the fully countersigned PDF.</p>
              <div className="flex gap-2 flex-wrap">
                <input
                  data-testid="speaking-signed-url-input"
                  type="url"
                  value={signedUrl}
                  onChange={(e) => setSignedUrl(e.target.value)}
                  placeholder="https://drive.google.com/…"
                  className="flex-1 min-w-[240px] bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900"
                />
                <button onClick={saveSignedUrl} disabled={busy} data-testid="speaking-save-signed-url"
                  className="border border-neutral-300 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">
                  Save
                </button>
                {eng.signed_agreement_url && (
                  <a href={eng.signed_agreement_url} target="_blank" rel="noreferrer" data-testid="speaking-view-signed-url"
                     className="border border-neutral-300 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">
                    View Signed Agreement
                  </a>
                )}
              </div>
              {eng.signed_agreement_received_at && (
                <div className="text-[11px] text-neutral-600 mt-2">Recorded {new Date(eng.signed_agreement_received_at).toLocaleString()}</div>
              )}
            </Section>
          )}

          {/* Payments */}
          {(eng.deposit_url || eng.balance_url || eng.deposit_paid_at || eng.balance_paid_at) && (
            <Section title="Payments">
              {eng.deposit_url && (
                <PaymentRow
                  label="Deposit"
                  amount={money(terms.deposit_cents, terms.currency)}
                  url={eng.deposit_url}
                  paidAt={eng.deposit_paid_at}
                  onCopy={() => copyToClipboard(eng.deposit_url, 'Deposit link')}
                  testId="deposit"
                />
              )}
              {eng.balance_url && (
                <PaymentRow
                  label="Balance"
                  amount={money(terms.balance_cents, terms.currency)}
                  url={eng.balance_url}
                  paidAt={eng.balance_paid_at}
                  onCopy={() => copyToClipboard(eng.balance_url, 'Balance link')}
                  testId="balance"
                />
              )}
              {(eng.payments || []).length > 0 && (
                <details className="mt-3">
                  <summary className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 cursor-pointer">View Stripe payment log ({eng.payments.length})</summary>
                  <div className="mt-2 space-y-2">
                    {eng.payments.map((p) => (
                      <div key={p.id} className="text-[11px] text-neutral-700 p-2 bg-white border border-neutral-200 rounded">
                        <strong className="uppercase tracking-[0.18em] text-[9px] text-neutral-500">{p.kind}</strong> · {p.status} · {money(p.amount_paid_cents || p.amount_cents, p.currency)} · <span className="font-mono">{p.stripe_session_id}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </Section>
          )}

          {/* Activity Timeline */}
          <Section title="Activity Timeline">
            <div data-testid="speaking-activity-timeline" className="relative pl-6 border-l border-neutral-200 space-y-3">
              {(eng.activity_log || []).length === 0 && <div className="text-[12px] text-neutral-500 italic">No activity yet.</div>}
              {(eng.activity_log || []).slice().reverse().map((a, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[27px] top-1 w-2.5 h-2.5 rounded-full bg-[#C8A15A] border-2 border-[#FAF8F5]" />
                  <div className="text-[13px] text-neutral-900">{TIMELINE_LABELS[a.action] || a.action.replace(/_/g, ' ')}</div>
                  {a.detail && <div className="text-[11px] text-neutral-500 mt-0.5 break-words">{a.detail}</div>}
                  <div className="text-[10px] text-neutral-400 mt-0.5 uppercase tracking-[0.14em]">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* Private Notes */}
          <Section title="Private Notes">
            <textarea data-testid="speaking-notes-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" />
            <button onClick={saveNotes} disabled={busy} data-testid="speaking-save-notes-btn" className="mt-3 border border-neutral-300 uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">Save Notes</button>
          </Section>
        </div>

        {showTerms && <TermsEditor eng={eng} onClose={() => setShowTerms(false)} onSaved={async () => { setShowTerms(false); onChanged(); await load(); }} />}
      </aside>
    </>
  );
}

const TIMELINE_LABELS = {
  status_change: 'Status changed',
  accepted: 'Inquiry accepted',
  declined: 'Inquiry declined',
  terms_updated: 'Engagement terms updated',
  agreement_sent: 'Agreement sent',
  agreement_resent: 'Agreement re-sent',
  agreement_acknowledged: 'Client acknowledged agreement',
  deposit_checkout_created: 'Deposit checkout session created',
  balance_checkout_created: 'Balance checkout session created',
  deposit_paid: 'Deposit paid',
  balance_paid: 'Balance paid',
  balance_requested: 'Balance payment link sent',
  signed_agreement_received: 'Signed agreement URL saved',
  completed: 'Engagement completed',
};

function TermsEditor({ eng, onClose, onSaved }) {
  const t = eng.engagement_terms || {};
  const [form, setForm] = useState({
    speaking_fee: centsToDollars(t.speaking_fee_cents),
    deposit: centsToDollars(t.deposit_cents),
    balance: centsToDollars(t.balance_cents),
    event_date: t.event_date || eng.event_date || '',
    event_location: t.event_location || eng.event_location || '',
    speaking_format: t.speaking_format || eng.event_format || '',
    speaking_duration: t.speaking_duration || '',
    deposit_due_date: t.deposit_due_date || '',
    balance_due_date: t.balance_due_date || '',
    travel_terms: t.travel_terms || '',
    accommodation_terms: t.accommodation_terms || '',
    cancellation_terms: t.cancellation_terms || '',
    additional_terms: t.additional_terms || '',
    internal_notes: t.internal_notes || '',
  });
  const [busy, setBusy] = useState(false);

  const fee = dollarsToCents(form.speaking_fee);
  const dep = dollarsToCents(form.deposit);
  const bal = form.balance === '' ? Math.max(0, fee - dep) : dollarsToCents(form.balance);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        speaking_fee_cents: fee,
        deposit_cents: dep,
        balance_cents: bal,
        event_date: form.event_date || null,
        event_location: form.event_location || null,
        speaking_format: form.speaking_format || null,
        speaking_duration: form.speaking_duration || null,
        deposit_due_date: form.deposit_due_date || null,
        balance_due_date: form.balance_due_date || null,
        travel_terms: form.travel_terms || null,
        accommodation_terms: form.accommodation_terms || null,
        cancellation_terms: form.cancellation_terms || null,
        additional_terms: form.additional_terms || null,
        internal_notes: form.internal_notes || null,
      };
      await adminApi.put(`/admin/speaking-engagements/${eng.id}/terms`, payload);
      toast.success('Engagement terms saved.');
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="terms-editor-modal" className="fixed inset-0 z-[60] bg-neutral-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#FAF8F5] rounded-2xl max-w-[720px] w-full max-h-[92vh] overflow-y-auto border border-neutral-200/70">
        <div className="px-6 py-5 border-b border-neutral-200/60 flex items-center justify-between sticky top-0 bg-[#FAF8F5]/95 backdrop-blur">
          <div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-1">Engagement Terms</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-xl text-neutral-900">{eng.name} · {eng.event_name || 'Speaking Engagement'}</div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900 text-2xl">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Speaking Fee (USD)" testId="fee">
              <input type="number" step="0.01" min="0" value={form.speaking_fee} onChange={(e) => set('speaking_fee', e.target.value)} data-testid="terms-fee" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" />
            </Field>
            <Field label="Deposit (USD)" testId="deposit">
              <input type="number" step="0.01" min="0" value={form.deposit} onChange={(e) => set('deposit', e.target.value)} data-testid="terms-deposit" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" />
            </Field>
            <Field label="Balance (auto if blank)">
              <input type="number" step="0.01" min="0" value={form.balance} onChange={(e) => set('balance', e.target.value)} placeholder={`Auto: $${((Math.max(0, fee - dep)) / 100).toFixed(2)}`} data-testid="terms-balance" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Event Date"><input value={form.event_date} onChange={(e) => set('event_date', e.target.value)} data-testid="terms-event-date" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" placeholder="e.g., October 12, 2026" /></Field>
            <Field label="Location"><input value={form.event_location} onChange={(e) => set('event_location', e.target.value)} data-testid="terms-location" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" placeholder="City, Venue" /></Field>
            <Field label="Format"><input value={form.speaking_format} onChange={(e) => set('speaking_format', e.target.value)} data-testid="terms-format" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" placeholder="Keynote, Panel…" /></Field>
            <Field label="Duration"><input value={form.speaking_duration} onChange={(e) => set('speaking_duration', e.target.value)} data-testid="terms-duration" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" placeholder="60 minutes" /></Field>
            <Field label="Deposit Due Date"><input value={form.deposit_due_date} onChange={(e) => set('deposit_due_date', e.target.value)} data-testid="terms-deposit-due" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" placeholder="Upon acknowledgment" /></Field>
            <Field label="Balance Due Date"><input value={form.balance_due_date} onChange={(e) => set('balance_due_date', e.target.value)} data-testid="terms-balance-due" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" placeholder="14 days before event" /></Field>
          </div>

          <Field label="Travel Terms"><textarea rows={2} value={form.travel_terms} onChange={(e) => set('travel_terms', e.target.value)} data-testid="terms-travel" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" /></Field>
          <Field label="Accommodation Terms"><textarea rows={2} value={form.accommodation_terms} onChange={(e) => set('accommodation_terms', e.target.value)} data-testid="terms-accommodation" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" /></Field>
          <Field label="Cancellation Terms"><textarea rows={2} value={form.cancellation_terms} onChange={(e) => set('cancellation_terms', e.target.value)} data-testid="terms-cancellation" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" /></Field>
          <Field label="Additional Terms"><textarea rows={3} value={form.additional_terms} onChange={(e) => set('additional_terms', e.target.value)} data-testid="terms-additional" className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]" placeholder="Anything else that should appear on the client's agreement page" /></Field>
          <Field label="Internal Notes (never shown to client)"><textarea rows={2} value={form.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} data-testid="terms-internal" className="w-full bg-white border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 text-[13px]" /></Field>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200/60 flex items-center justify-end gap-2 sticky bottom-0 bg-[#FAF8F5]/95 backdrop-blur">
          <button onClick={onClose} className="uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-100">Cancel</button>
          <button onClick={save} disabled={busy || fee <= 0} data-testid="terms-save-btn" className="bg-neutral-900 text-white uppercase tracking-[0.2em] text-[10px] px-4 py-2 rounded-full hover:bg-neutral-800 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save Engagement Terms'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentRow({ label, amount, url, paidAt, onCopy, testId }) {
  return (
    <div className="p-3 bg-white border border-neutral-200 rounded-lg mb-2" data-testid={`payment-row-${testId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">{label}</div>
          <div className="text-neutral-900 tabular-nums">{amount}</div>
        </div>
        <div className="flex items-center gap-2">
          {paidAt ? (
            <span className="text-[11px] text-emerald-800 uppercase tracking-[0.18em]">✓ Paid {new Date(paidAt).toLocaleDateString()}</span>
          ) : (
            <>
              <a href={url} target="_blank" rel="noreferrer" data-testid={`payment-open-${testId}`} className="border border-neutral-300 uppercase tracking-[0.18em] text-[10px] px-3 py-1.5 rounded-full hover:bg-neutral-100">Open</a>
              <button onClick={onCopy} data-testid={`payment-copy-${testId}`} className="border border-neutral-300 uppercase tracking-[0.18em] text-[10px] px-3 py-1.5 rounded-full hover:bg-neutral-100">Copy</button>
            </>
          )}
        </div>
      </div>
      <div className="text-[11px] text-neutral-500 mt-1 break-all">{url}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 text-[13px]">
      <div className="w-32 shrink-0 text-neutral-500 text-[11px] uppercase tracking-[0.18em]">{label}</div>
      <div className="text-neutral-800 break-words min-w-0">{value || '—'}</div>
    </div>
  );
}
function Block({ label, value }) {
  return (
    <div>
      <div className="text-neutral-500 text-[11px] uppercase tracking-[0.18em] mb-1">{label}</div>
      <div className="text-[13px] text-neutral-800 whitespace-pre-wrap">{value || <em className="text-neutral-400">—</em>}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="uppercase tracking-[0.18em] text-[10px] text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
function Fee({ label, value, accent }) {
  return (
    <div className={`p-3 rounded-lg text-center ${accent ? 'bg-[#C8A15A]/10 border border-[#C8A15A]/30' : 'bg-neutral-100'}`}>
      <div className="uppercase tracking-[0.18em] text-[9px] text-neutral-500 mb-1">{label}</div>
      <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className={`text-lg tabular-nums ${accent ? 'text-[#8a6c30]' : 'text-neutral-900'}`}>{value}</div>
    </div>
  );
}
